import { KLAYO_COLORS } from '@/themes';
import { Box, DialogActions } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import { ActionsContainer } from 'klayowebshared';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StandardLibraryTable } from '../../components/table/StandardLibraryTable';
import { TablePlaceholder } from '../../components/TablePlaceholder';
import { useAppMutation } from '../../hooks/useAppMutation';
import { IJobItem, ISuccessImportJob } from '../../interfaces/Job';
import { useLocale } from '../../providers/LocaleProvider';
import { useSnackbar } from '../../providers/SnackbarProvider';
import BriefcaseIcons from '../../resources/images/icons-briefcase.svg';
import ImportJobCopyDialog from './ImportJobCopyDialog';

export type StandardMatchedJobsProps = {
  theme: any;
  jobs: IJobItem[];
  onClose?: () => void;
  hideActions?: boolean;
  onSelectionChange?: (jobId: string | null) => void;
};

export type StandardMatchedJobsRef = {
  getSelectedJobId: () => GridRowSelectionModel;
};

const StandardMatchedJobs = ({
  theme,
  jobs,
  onClose,
  hideActions,
  onSelectionChange,
}: StandardMatchedJobsProps) => {
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [showImportJobCopy, setShowImportJobCopy] = useState(false);

  const { showSnackbar } = useSnackbar();
  const { translate } = useLocale();
  const navigate = useNavigate();

  const { mutate: onImportJob, isPending: isLoading } = useAppMutation<
    AxiosResponse<ISuccessImportJob>,
    any,
    {
      jobDefinitionId: string;
      taskIds: string[];
      competencyIds: string[];
      isOverriding?: boolean;
    }
  >('post', '/StandardLibrary/jobs/import', {
    onSuccess: (response: AxiosResponse<ISuccessImportJob>) => {
      if (response?.data?.isSuccess) {
        showSnackbar(translate('messages.jobImportSuccess'), 'success');
        onClose?.();
        navigate(`/settings/jobs/edit/${response.data.jobDefinition.jobDefinitionId}`);
      } else {
        showSnackbar(response.data.errorMessage || translate('messages.jobImportError'), 'error');
      }
      setShowImportJobCopy(false);
    },
    onError: (error) => {
      if (error.status === 409) {
        setShowImportJobCopy(true);
      } else {
        showSnackbar(error.response.data.message || translate('messages.jobImportError'), 'error');
      }
    },
  });

  const onSelectedJob = (_: any, row: any) => {
    setSelectedRows((prev) => {
      const next = prev === row.id ? [] : [row.id];
      onSelectionChange?.(row.id);
      return next;
    });
  };

  const onImportAndEditJob = (isOverriding = false) => {
    if (isEmpty(selectedRows)) return;

    onImportJob({
      jobDefinitionId: selectedRows[0].toString(),
      taskIds: [],
      competencyIds: [],
      isOverriding: isOverriding,
    });
  };

  const onCloseImportJobCopy = () => {
    setShowImportJobCopy(false);
  };

  return (
    <Box
      sx={{
        '& .MuiDataGrid-row': {
          border: `2px solid transparent !important`,
          boxSizing: 'border-box',

          '&[aria-selected="true"]': {
            borderColor: `${KLAYO_COLORS.NeonBlueNB500} !important`,
            backgroundColor: `${KLAYO_COLORS.WhiteWH900} !important`,
          },
        },
      }}
    >
      <StandardLibraryTable
        theme={theme}
        columns={[{ type: StandardLibraryTable.columns.jobSummary }]}
        rows={jobs}
        isLoading={false}
        rowHeight={82}
        dense={true}
        paper={false}
        onJobSelect={onSelectedJob}
        pagination={false}
        showHeader={false}
        showFooter={false}
        hideFirstLastBorder
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer !important' } }}
        noResultsComponent={
          <TablePlaceholder
            text={{
              primary: translate('standardsLibrary.noMatchingJobs'),
              secondary: translate('standardsLibrary.noMatchingJobsDesc'),
            }}
            icon={<BriefcaseIcons />}
          />
        }
        noRowsComponent={
          <TablePlaceholder
            text={{
              primary: translate('standardsLibrary.noMatchingJobs'),
              secondary: translate('standardsLibrary.noMatchingJobsDesc'),
            }}
            icon={<BriefcaseIcons />}
          />
        }
        translate={translate}
      />

      {!hideActions && (
        <DialogActions
          sx={{
            padding: { xs: '24px 0', md: '40px 0' },
          }}
        >
          <ActionsContainer
            actions={[
              {
                label: translate('standardsLibrary.importAndEdit'),
                primary: true,
                variant: 'filled',
                onClick: () => onImportAndEditJob(),
                disabled: (!showImportJobCopy && isLoading) || isEmpty(selectedRows),
                isLoading: !showImportJobCopy && isLoading,
              },
              {
                label: translate('common.cancel'),
                onClick: onClose,
                disabled: !showImportJobCopy && isLoading,
              },
            ]}
            justifyActions='right'
            onClose={onClose}
          />
        </DialogActions>
      )}

      <ImportJobCopyDialog
        open={showImportJobCopy}
        loading={isLoading}
        onConfirm={() => onImportAndEditJob(true)}
        onClose={onCloseImportJobCopy}
        theme={theme}
      />
    </Box>
  );
};
export default StandardMatchedJobs;
